import type { Node as PmNode } from '@tiptap/pm/model';

import {
  TIPTAP_ACTION_BUTTON_TYPE,
  TIPTAP_FIGURE_TYPE,
  TIPTAP_LINK_BUTTON_TYPE,
} from './../../ricos-content/index.js';
import type { Node, PluginContainerData } from './../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { getContainerData } from './../../_shared/node-styles.js';
import { PmNodeType } from './../../_shared/pm-node-types.js';

const CLOSED_FOR_SPACING_TYPES: readonly (Node_Type | string)[] = [
  TIPTAP_FIGURE_TYPE,
  TIPTAP_LINK_BUTTON_TYPE,
  TIPTAP_ACTION_BUTTON_TYPE,
  Node_Type.IMAGE,
  Node_Type.VIDEO,
  Node_Type.GALLERY,
  Node_Type.DIVIDER,
  Node_Type.HTML,
  Node_Type.EMBED,
  Node_Type.FILE,
  Node_Type.GIF,
  Node_Type.AUDIO,
  Node_Type.POLL,
  Node_Type.BUTTON,
  Node_Type.ORDERED_LIST,
  Node_Type.BULLETED_LIST,
  Node_Type.LIST_ITEM,
  Node_Type.LINK_PREVIEW,
  Node_Type.APP_EMBED,
  Node_Type.CODE_BLOCK,
  Node_Type.BLOCKQUOTE,
  Node_Type.TABLE,
  Node_Type.TABLE_CELL,
  Node_Type.LAYOUT,
  Node_Type.LAYOUT_CELL,
  Node_Type.COLLAPSIBLE_LIST,
  Node_Type.COLLAPSIBLE_ITEM_BODY,
  Node_Type.SHAPE,
] as const;

export const isNodeSpaced = (nodeType: string): boolean =>
  CLOSED_FOR_SPACING_TYPES.includes(nodeType);

/**
 * Nodes that have UI elements overflowing above them when selected:
 * - IMAGE, SHAPE: Top resize handles (5px overflow)
 * - LAYOUT: Column drag handlers (9px overflow)
 */
const NODES_WITH_TOP_OVERFLOW: readonly (Node_Type | string)[] = [
  Node_Type.IMAGE,
  Node_Type.SHAPE,
  Node_Type.LAYOUT,
] as const;

/**
 * For viewer - checks node type string directly
 */
export const needsTopMargin = (nodeType?: string | null): boolean =>
  nodeType ? NODES_WITH_TOP_OVERFLOW.includes(nodeType as Node_Type) : false;

/**
 * For editor - checks ProseMirror node, including FIGURE's first child.
 * In ProseMirror, IMAGE nodes are wrapped in FIGURE nodes, so we need to
 * check the first child to determine if it's an IMAGE with top resize handles.
 */
export const needsTopMarginForPmNode = (node?: PmNode | null): boolean => {
  if (!node) return false;

  const nodeType = node.type.name;

  // Direct match for SHAPE, LAYOUT
  if (NODES_WITH_TOP_OVERFLOW.includes(nodeType as Node_Type)) {
    return true;
  }

  // Check FIGURE's first child for IMAGE (which has verticallyResizable: true)
  if (nodeType === TIPTAP_FIGURE_TYPE) {
    const firstChild = node.firstChild;
    return firstChild?.type.name === Node_Type.IMAGE;
  }

  return false;
};

export const isCollapsedBetween = (
  firstNodeType?: string | null,
  secondNodeType?: string | null,
  parentNodeType?: string | null,
  options?: { bothFullWidth?: boolean }
): boolean => {
  const isEdgeNode = !firstNodeType || !secondNodeType;

  if (isEdgeNode && parentNodeType !== Node_Type.LIST_ITEM) {
    // Collapse edge gap spacers everywhere except in list items. List items look ugly when collapsed.
    return true;
  }

  // Collapse gap between any two full-width (extended) elements
  if (options?.bothFullWidth) {
    return true;
  }

  return false;
};

const getNodeContainerData = (node: Node | PmNode): PluginContainerData | undefined | null => {
  // Handle PM nodes
  if ('attrs' in node && node.attrs) {
    // If it's a figure, we need its' first child's container data
    if (node.type.name === PmNodeType.FIGURE) {
      return node.firstChild?.attrs?.containerData;
    }

    return node.attrs.containerData;
  }

  if ('type' in node && 'id' in node) {
    return getContainerData(node);
  }

  return null;
};

export const isNodeFloated = (node?: Node | PmNode | null): boolean => {
  if (!node) return false;

  const containerData = getNodeContainerData(node);
  const hasTextWrap = containerData?.textWrap ?? true;
  const isFloatableAlignment =
    containerData?.alignment === 'LEFT' || containerData?.alignment === 'RIGHT';

  return hasTextWrap && isFloatableAlignment;
};

export const isBetweenFloatedNodes = (
  node1?: Node | PmNode | null,
  node2?: Node | PmNode | null
): boolean => isNodeFloated(node1) && isNodeFloated(node2);

const getNodeType = (node: Node | PmNode): string => {
  // PM node
  if ('attrs' in node && node.type?.name) {
    // For FIGURE nodes, get the actual content type from first child
    if (node.type.name === PmNodeType.FIGURE) {
      return node.firstChild?.type.name || node.type.name;
    }
    return node.type.name;
  }
  // Ricos node
  if ('type' in node && 'id' in node) {
    return (node as Node).type;
  }
  return '';
};

/**
 * Check if a node is full-width (extended).
 * Layouts are always full-width.
 * Other plugin nodes are full-width when containerData.width.size === 'FULL_WIDTH'.
 */
export const isNodeFullWidth = (node?: Node | PmNode | null): boolean => {
  if (!node) return false;

  const nodeType = getNodeType(node);

  // Layouts are always full-width (breakout)
  if (nodeType === Node_Type.LAYOUT) {
    return true;
  }

  const containerData = getNodeContainerData(node);
  return containerData?.width?.size === 'FULL_WIDTH';
};

export const isBetweenFullWidthNodes = (
  node1?: Node | PmNode | null,
  node2?: Node | PmNode | null
): boolean => isNodeFullWidth(node1) && isNodeFullWidth(node2);
